// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { grpc } from "../../../app/rpc/executor"
import { assistantClient } from "../../../app/rpc/client"
import { showSnackBar } from "../../snackbar/actions"
import { isGrpcError } from "../../../lib/rpcutils"
import {
  BatchTranslateTextsRequest,
  BatchTranslateTextsResponse,
} from "../../../generated/wisetime/user/assistant/v4/assistant_pb"

type TranslateNarrativesRpc = (request: BatchTranslateTextsRequest) => Observable<BatchTranslateTextsResponse>

export const translateNarratives = (translateNarrativesRpc: TranslateNarrativesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.translateNarratives.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(translateNarrativesRpc),
          map(response => response.toObject().translationsList),
          map(TimelineAdditionalUiSlice.setPendingNarratives),
          catchError(e => handleError(e, action.payload)),
        )
      ),
    )

const handleError = (e: unknown, request: BatchTranslateTextsRequest): Observable<Action> => {
  const actions: Action[] = [
    TimelineAdditionalUiSlice.deletePendingNarratives(request.toObject().translationsList.map(t => t.key)),
  ]
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(showSnackBar({
        message: t({ id: "msg_translate_narratives_permission_denied", message: "Failed to translate narrative(s). You do not have permission." }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({ id: "msg_translate_narratives_failed", message: "Failed to translate narrative(s). Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const translateNarrativesRpc = (request: BatchTranslateTextsRequest): Observable<BatchTranslateTextsResponse> =>
  grpc(() => assistantClient.batchTranslateTexts(request, null))

export default translateNarratives(translateNarrativesRpc)
